import { Component, OnInit } from '@angular/core';
import { Observable, Subject, takeUntil } from 'rxjs';
// State
import { Store } from '@ngxs/store';
import { RulesState } from '../../../rules/state/rules.state';
import { QualityState } from '../../../state/quality.state';
// Model
import { ORDER_ACTIONS_MESSAGE, ORDER_STATE } from '../../../model/quality.constant';
import { OrderDetailModel } from '../../../state/model/order-detail.model';
import { OrdersRulesModel } from '../../../rules/model/ci-rule.model';
import { RULE_STATUS, RULESET_NAMES } from '../../../rules/model/constants';
import { APPROVE_ORDER_EVENT, UPDATE_REJECTED_SUMMARY } from '../model/sub-header.constant';
// Modules
import { BroadcastService, ORDER_EVENT_TYPE, ToasterType } from '@cv-invision/core';
// Service
import { QualityDocumentService } from '../../../service/document-quality.service';
import { OverviewSubHeaderService } from '../service/overview-sub-header.service';

@Component({
	selector: 'cv-approve-reject-buttons',
	templateUrl: './approve-reject-buttons.component.html',
	styleUrls: ['./approve-reject-buttons.component.scss'],
	host: {
		class: 'd-inline-flex align-items-center position-relative'
	}
})
export class ApproveRejectButtonsComponent implements OnInit {
	orderDetail$: Observable<OrderDetailModel> = this.store.select(QualityState.getOrderDetail);
	orderRules$: Observable<OrdersRulesModel> = this.store.select(RulesState.getOrderRules);
	isRecalled$: Observable<boolean> = this.store.select(QualityState.getIsbeingRecalled);

	orderDetail: OrderDetailModel;
	orderStatus = ORDER_STATE;
	orderRules: OrdersRulesModel;
	approveBtn: string;
	rejectBtn: string;
	isRecallingBtn: boolean;

	clientNote: string;
	rejectionSummary: string;

	rejectionBoxOpen: boolean;
	disabledReject = true;
	disabledApprove = true;

	get lockedBy(): any {
		return this.overviewSubheaderService.orderLocked;
	}

	private atComponentDestroy = new Subject();

	constructor(
		private qualityDocumentService: QualityDocumentService,
		private broadcastService: BroadcastService,
		private store: Store,
		private overviewSubheaderService: OverviewSubHeaderService
	) {
		this.clientNote = '';
		this.rejectionSummary = '';
		this.rejectionBoxOpen = false;
		this.approveBtn = ORDER_STATE.APPROVE;
		this.rejectBtn = ORDER_STATE.REJECT;
	}

	ngOnInit(): void {
		this.orderDetail$.pipe(takeUntil(this.atComponentDestroy)).subscribe((orderDetail: OrderDetailModel) => {
			const { stateEnum } = orderDetail;
			this.orderDetail = orderDetail;

			if (stateEnum === ORDER_STATE.REJECTED) {
				this.rejectBtn = ORDER_STATE.REJECTED;
			}
			if (stateEnum === ORDER_STATE.APPROVED) {
				this.approveBtn = ORDER_STATE.APPROVED;
			}

			if (!this.lockedBy?.byTheUser) {
				this.disabledApprove = true;
				this.disabledReject = true;
				return;
			}

			// Post-revision override
			if (stateEnum === ORDER_STATE.POST_REVISION_RECEIVED) {
				const violations = this.orderRules?.violation || [];

				this.applyPostRevisionOverride(violations);
				return;
			}

			if (
				[
					ORDER_STATE.INQUALITY.toString(),
					ORDER_STATE.REOPEN.toString(),
					ORDER_STATE.QUEUED.toString()
				].indexOf(stateEnum) > -1
			) {
				this.disabledApprove = false;

				if (this.orderRules && this.orderRules.violation && this.orderRules.violation.length) {
					const rejectAppr = this.orderRules.violation.filter((v) => v.status === RULE_STATUS.FAILED);
					if (this.lockedBy.byTheUser) {
						this.disabledReject = !rejectAppr.length;
					}
				} else {
					this.disabledReject = true;
				}
			}
		});

		this.orderRules$.pipe(takeUntil(this.atComponentDestroy)).subscribe((oRules) => {
			this.orderRules = oRules;

			if (this.orderDetail && this.orderDetail.stateEnum === ORDER_STATE.POST_REVISION_RECEIVED) {
				if (!this.lockedBy?.byTheUser) {
					this.disabledReject = true;
					this.disabledApprove = true;
					return;
				}

				const violations = oRules?.violation || [];
				this.applyPostRevisionOverride(violations);
				return;
			}

			if (oRules && oRules.violation && oRules.violation.length) {
				const rejectAppr = oRules.violation.filter((v) => v.status === RULE_STATUS.FAILED);
				if (this.lockedBy.byTheUser) {
					this.disabledReject = !rejectAppr.length;
				}
			}
		});

		this.isRecalled$.pipe(takeUntil(this.atComponentDestroy)).subscribe((isC) => {
			this.isRecallingBtn = isC;
			if (isC) {
				this.rejectBtn = ORDER_STATE.RECALLED;
			}
		});

		this.overviewSubheaderService.subheaderEvents
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((event: any) => {
				switch (event.name) {
					case ORDER_EVENT_TYPE.ORDER_REJECTED:
						this.rejectBtn = ORDER_STATE.REJECTED;
						this.disabledReject = true;
						break;
					case ORDER_EVENT_TYPE.ORDER_APPROVED:
						this.approveBtn = ORDER_STATE.APPROVED;
						this.disabledApprove = true;
						break;
					case ORDER_EVENT_TYPE.ORDER_RESUBMITTED: {
						const { isValid } = event.data;
						if (isValid !== undefined && isValid === false) {
							this.isRecallingBtn = false;
							this.rejectBtn = ORDER_STATE.REJECTED;
							break;
						}
						this.rejectBtn = ORDER_STATE.REJECT;
						this.approveBtn = ORDER_STATE.APPROVE;
						this.disabledApprove = false;
						this.disabledReject = true;
						break;
					}
				}
			});

		this.broadcastService
			.onEvent(UPDATE_REJECTED_SUMMARY)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(() => {
				if (this.rejectionBoxOpen) {
					this.qualityDocumentService.getOrderSummary(this.orderDetail.id).subscribe((response) => {
						this.rejectionSummary = (response?.note || '').replace(/<br\s*\/?>/gi, '\n');
					});
				}
			});
	}

	/**
	 * Approve the current order
	 */
	approveOrder(): void {
		if (this.approveValidations()) {
			this.openModalApprove();
		} else {
			this.disabledApprove = true;
			this.qualityDocumentService.approveOrder(this.orderDetail.id).subscribe((r) => {
				if (r.isValid) {
					this.broadcastService.broadcast({
						name: ToasterType.SUCCESS,
						event: {
							message: ORDER_ACTIONS_MESSAGE.APPROVED
						}
					});
					this.overviewSubheaderService.subheaderEvents.next({ name: 'RETURN_HOME' });
				} else {
					this.disabledApprove = false;
				}
			});
		}
	}

	/**
	 * reject the current order
	 */
	rejectOrder(): void {
		if (!this.disabledReject) {
			this.disabledReject = true;
		}
		const rejectParams = {
			note: this.clientNote,
			rejectionSummary: this.rejectionSummary
		};
		this.qualityDocumentService.rejectOrder(this.orderDetail.id, rejectParams).subscribe((r) => {
			if (r.isValid) {
				this.broadcastService.broadcast({
					name: ToasterType.SUCCESS,
					event: {
						message: ORDER_ACTIONS_MESSAGE.REJECTED
					}
				});
				this.overviewSubheaderService.subheaderEvents.next({ name: 'RETURN_HOME' });
			}
		});
	}

	/**
	 * open rejection box
	 */
	toggleRejectionBox(): void {
		if (this.orderDetail.stateEnum !== this.orderStatus.REJECTED) {
			this.qualityDocumentService.getOrderSummary(this.orderDetail.id).subscribe((response) => {
				this.rejectionSummary = (response?.note || '').replace(/<br\s*\/?>/gi, '\n');
			});

			this.rejectionBoxOpen = !this.rejectionBoxOpen;
			this.disabledApprove = this.rejectionBoxOpen;
		}
	}

	/**
	 * validate if either approve or reject buttons can be enabled
	 */
	private approveValidations(): boolean {
		const canSave = this.store.selectSnapshot(RulesState.getRulesCanSaveSate);
		const failedR = this.store
			.selectSnapshot(RulesState.getOrderRules)
			.violation.filter((v) => v.status === RULE_STATUS.FAILED);
		const sv = canSave.filter((r) => !r.canSave);

		if (sv.length || failedR.length) {
			return true;
		}
		return false;
	}

	/**
	 * open approve modal order
	 */
	openModalApprove(): void {
		this.broadcastService.broadcast({
			name: APPROVE_ORDER_EVENT,
			event: {
				data: this.orderDetail
			}
		});
	}

	/**
	 * Updates approve/reject disabled flags based on POST_REVISION rules.
	 * @param violations
	 */
	private applyPostRevisionOverride(violations: any[]): void {
		const postRevisionRules = violations.filter((v) => v.ruleSetName === RULESET_NAMES.POST_REVISION);

		// If no post-revision rules exist, disable both actions.
		if (!postRevisionRules.length) {
			this.disabledReject = true;
			this.disabledApprove = true;
			return;
		}

		const hasFailed = postRevisionRules.some((v) => v.status === RULE_STATUS.FAILED);
		const allPassed = postRevisionRules.every((v) => v.status === RULE_STATUS.PASSED);

		// Reject is enabled when at least one POST_REVISION rule failed.
		if (hasFailed) {
			this.disabledReject = false;
			this.disabledApprove = true;
			return;
		}

		// Approve is enabled only when all POST_REVISION rules passed.
		if (allPassed) {
			this.disabledReject = true;
			this.disabledApprove = false;
			return;
		}

		this.disabledReject = true;
		this.disabledApprove = true;
	}
}
